/**
 * Reclaim Our Presence — Landing Page
 *
 * The preliminary entry-point at `/reclaim-presence`. Two big
 * doorways: either browse the 13 short **Daily Awareness Activations**
 * (2-minute self-contained rituals — Hydration, Cacao Ceremony, Breath
 * Reset, etc.) or step into the **21-Day Immersion Program**
 * (progressive daily journey).
 *
 * Design tenets:
 *   · Two-choice symmetry — equal visual weight, no "hero" pick
 *   · Warm forest-green base to match Reclaim Presence identity
 *   · Micro-copy tells the user WHY they'd pick each — not just labels
 *   · Live counters (ready/total activations · current day of program)
 *     pulled from the backend so it feels alive, not static
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Headphones, Sparkles, Leaf } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PresenceLanding() {
  const navigate = useNavigate();
  const [activationsMeta, setActivationsMeta] = useState({ ready_count: 0, total: 0 });
  const [programMeta, setProgramMeta] = useState({ current_day: null, total_days: 21 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/presence/activations`);
        if (r.ok && !cancelled) {
          const d = await r.json();
          setActivationsMeta({ ready_count: d.ready_count || 0, total: d.total || 0 });
        }
      } catch (_) {}
      try {
        const token = localStorage.getItem('reclaim_token');
        if (!token) return;
        const r = await fetch(`${API}/api/presence/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancelled) {
          const d = await r.json();
          setProgramMeta({
            current_day: d?.current_day ?? null,
            total_days: d?.total_days ?? 21,
          });
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const dayLabel = programMeta.current_day
    ? `Day ${programMeta.current_day} of ${programMeta.total_days}`
    : `${programMeta.total_days}-day journey`;

  return (
    <div
      className="min-h-screen text-[#F2EFE9]"
      style={{
        background:
          'radial-gradient(ellipse at top, #1A2622 0%, #0F1A16 55%, #060B09 100%)',
        fontFamily: '"Manrope", sans-serif',
      }}
      data-testid="presence-landing-page"
    >
      {/* Header */}
      <div className="pt-6 pb-2 px-6 text-center">
        <button
          onClick={() => navigate('/')}
          className="text-2xl leading-none absolute top-6 left-6 opacity-60 hover:opacity-100"
          data-testid="presence-landing-back"
        >
          ←
        </button>
        <p
          className="uppercase tracking-[0.4em] text-[10px] opacity-70"
          style={{ color: '#7EE2C8' }}
        >
          Reclaim Our Presence
        </p>
      </div>

      {/* Intro */}
      <div className="max-w-lg mx-auto px-6 pt-10 text-center">
        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-6"
          style={{ background: 'rgba(126, 226, 200, 0.10)', border: '1px solid rgba(126, 226, 200, 0.25)' }}>
          <Leaf size={26} style={{ color: '#7EE2C8' }} />
        </div>
        <h1
          className="italic leading-tight"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: 'clamp(2rem, 5vw, 2.75rem)',
            fontWeight: 400,
          }}
        >
          Choose your doorway
        </h1>
        <p className="mt-4 text-sm opacity-70 leading-relaxed max-w-md mx-auto">
          Reclaim Presence lives in two forms — take one when you have
          two minutes, take the other when you're ready for a longer
          walk home to yourself.
        </p>
      </div>

      {/* Two doorways */}
      <div className="max-w-lg mx-auto px-6 pt-10 pb-12 space-y-4">
        {/* ── Activations ── */}
        <motion.button
          onClick={() => navigate('/reclaim-presence/activations')}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.99 }}
          className="w-full text-left rounded-2xl p-6 flex items-start gap-5 transition-all border"
          style={{
            background: 'rgba(126, 226, 200, 0.06)',
            borderColor: 'rgba(126, 226, 200, 0.22)',
          }}
          data-testid="presence-landing-activations"
        >
          <div
            className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(140deg, rgba(126, 226, 200, 0.25), rgba(126, 226, 200, 0.08))',
              border: '1px solid rgba(126, 226, 200, 0.35)',
            }}
          >
            <Headphones size={22} style={{ color: '#B4F0DA' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <h2
                className="italic"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '1.5rem',
                  color: '#F2EFE9',
                }}
              >
                Activations
              </h2>
              <span
                className="text-[10px] uppercase tracking-wider opacity-70 flex-shrink-0"
                style={{ color: '#7EE2C8' }}
              >
                {activationsMeta.ready_count} of {activationsMeta.total} ready
              </span>
            </div>
            <p className="text-sm opacity-70 mt-1.5 leading-relaxed">
              13 short 2-minute audios — Hydration, Cacao Ceremony,
              Breath Reset, Walking, Nature, Gratitude, Sleep…
              Micro-rituals for the moments already in your day.
            </p>
            <div
              className="mt-3 text-[11px] flex items-center gap-1.5 opacity-80"
              style={{ color: '#B4F0DA' }}
            >
              Browse activations <ChevronRight size={14} />
            </div>
          </div>
        </motion.button>

        {/* ── Program ── */}
        <motion.button
          onClick={() => navigate('/reclaim-presence/program')}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.99 }}
          className="w-full text-left rounded-2xl p-6 flex items-start gap-5 transition-all border"
          style={{
            background: 'rgba(215, 187, 122, 0.05)',
            borderColor: 'rgba(215, 187, 122, 0.22)',
          }}
          data-testid="presence-landing-program"
        >
          <div
            className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(140deg, rgba(215, 187, 122, 0.25), rgba(215, 187, 122, 0.08))',
              border: '1px solid rgba(215, 187, 122, 0.35)',
            }}
          >
            <Sparkles size={22} style={{ color: '#F5D89E' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <h2
                className="italic"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '1.5rem',
                  color: '#F2EFE9',
                }}
              >
                21-Day Immersion
              </h2>
              <span
                className="text-[10px] uppercase tracking-wider opacity-70 flex-shrink-0"
                style={{ color: '#D7BB7A' }}
              >
                {dayLabel}
              </span>
            </div>
            <p className="text-sm opacity-70 mt-1.5 leading-relaxed">
              A progressive 21-day journey — morning immersion,
              midday reset, evening reflection, night audio. Guided
              state shifts that replace unconscious scrolling with
              conscious return.
            </p>
            <div
              className="mt-3 text-[11px] flex items-center gap-1.5 opacity-80"
              style={{ color: '#F5D89E' }}
            >
              {programMeta.current_day ? 'Continue journey' : 'Begin journey'} <ChevronRight size={14} />
            </div>
          </div>
        </motion.button>
      </div>
    </div>
  );
}
